import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
/**
 * Fix: Force Search Images to 'Contain' instead of 'Cover'
 * This prevents tall or wide products from being cropped in search results.
 */
const fixSearchImages = () => {
  // Target both search page and predictive search dropdown
  const selectors = [
    'results-list img',
    '.predictive-search img',
    '.predictive-search__item-content img',
    '.product-grid__item img'
  ];

  const images = document.querySelectorAll(selectors.join(','));

  images.forEach(img => {
    img.style.objectFit = 'contain';
    img.style.objectPosition = 'center';
    img.style.height = '100%';
    img.style.width = '100%';
    
    // Ensure the parent container is set to a square aspect ratio if needed
    const container = img.closest('.media');
    if (container) {
      container.style.aspectRatio = '1 / 1';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      container.style.justifyContent = 'center';
    }
  });
};

// Run when the script loads
fixSearchImages();

// Since search results are often loaded dynamically (AJAX), 
// we watch for changes in the document to re-apply the fix.
const observer = new MutationObserver(() => {
  fixSearchImages();
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});